/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CostCenterFinderListItem } from '@gh/builder-shared';
import { PriceBookUsage } from '@gh/builder-shared-data';
import {
  createGroupIndexDriver,
  createOrderIndexDriver,
  createTableDriver,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  hasFormStateError,
  isEntityInvalid,
  newEntityComparator,
  setNewDirtyType,
  Table,
  whenExisting,
  whenModified,
} from '@gh/common-shared';
import { DirtyType, EntityVersion, ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { composeComparators, valueComparator } from '@gh/core-util';
import { curryRight, isNil, overEvery } from 'lodash';
import {
  getPbCcStructureId,
  getPbCostCenterId,
  getPbHousePlanId,
  isCcNotPreloaded,
  PbCcStructure,
  PbCostCenter,
  PbDefaults,
  PbDetails,
  PbDetailsSaveResponse,
  PbHousePlan,
  PbHousePlanFilter,
  pbHousePlanToRowDto,
  PbMasterInfo,
  validatePbMasterInfo,
} from '../../resources/price-book.model';

export enum PbDetailsStateEventType {
  Load, MergeOnCalculate, MergeOnSave,
  SelectTab, NavigateToInvalid,
  SetMasterInfo, SetDefaults, SetRootField,
  UpdateHpFilter,
  HousePlanAddSet, HousePlanUpdate, HousePlanRemove, HousePlanSelect, CostCenterAddSet, CostCenterRemove, CostCenterSelect,
  CostCenterLoad,
  StructureSetField, StructureRemove, StructureSelect, ResetFocusedCell,
  StructureAdd,
}

export type PbDetailsStateLoadEvent = { type: PbDetailsStateEventType.Load; details: PbDetails; mode: FormMode; sourcePriceBookId?: ID };
export type PbDetailsStateMergeOnSaveEvent = { type: PbDetailsStateEventType.MergeOnSave; calculation: PbDetailsSaveResponse };
export type PbDetailsStateMergeOnCalculateEvent = { type: PbDetailsStateEventType.MergeOnCalculate; calculation: PbDetailsSaveResponse };
export type PbDetailsStateSelectTabEvent = { type: PbDetailsStateEventType.SelectTab; tab: PbDetailsTab };
export type PbDetailsStateNavigateToInvalidEvent = { type: PbDetailsStateEventType.NavigateToInvalid };
export type PbDetailsStateSetMasterInfoEvent = { type: PbDetailsStateEventType.SetMasterInfo; masterInfo: FormStateSnapshot<PbMasterInfo> };
export type PbDetailsStateSetDefaultsEvent = { type: PbDetailsStateEventType.SetDefaults; defaults: FormStateSnapshot<PbDefaults> };
export type PbDetailsStateSetRootFieldEvent = { type: PbDetailsStateEventType.SetRootField; field: keyof PbDetailsState; value: any };
export type PbDetailsStateUpdateHpFilterEvent = { type: PbDetailsStateEventType.UpdateHpFilter; field: keyof PbHousePlanFilter; value: any };
export type PbDetailsStateHousePlanAddSet = {
  type: PbDetailsStateEventType.HousePlanAddSet;
  housePlans: PbHousePlan[];
};
export type PbDetailsStateHousePlanUpdate = { type: PbDetailsStateEventType.HousePlanUpdate; housePlan: PbHousePlan };
export type PbDetailsStateHousePlanRemove = { type: PbDetailsStateEventType.HousePlanRemove; housePlan: PbHousePlan };
export type PbDetailsStateHousePlanSelect = { type: PbDetailsStateEventType.HousePlanSelect; housePlan?: PbHousePlan };
export type PbDetailsStateCostCenterAddEvent = { type: PbDetailsStateEventType.CostCenterAddSet; costCenters: PbCostCenter[] };
export type PbDetailsStateCostCenterRemoveEvent = { type: PbDetailsStateEventType.CostCenterRemove; costCenterId: ID };
export type PbDetailsStateCostCenterSelectEvent = { type: PbDetailsStateEventType.CostCenterSelect; costCenterId?: ID };
export type PbDetailsStateCostCenterLoadEvent = { type: PbDetailsStateEventType.CostCenterLoad; costCenterId: ID; structures: PbCcStructure[] };

export type PbDetailsStateStructureSetFieldEvent = { type: PbDetailsStateEventType.StructureSetField; structureId: ID; name: string; value: any };
export type PbDetailsStateStructureRemoveEvent = { type: PbDetailsStateEventType.StructureRemove; structureId: ID };
export type PbDetailsStateStructureAddEvent = { type: PbDetailsStateEventType.StructureAdd; structures: PbCcStructure[] };
export type PbDetailsStateStructureSelectEvent = { type: PbDetailsStateEventType.StructureSelect; reId?: ID };
export type PbDetailsStateDetailsResetFocusedCellEvent = { type: PbDetailsStateEventType.ResetFocusedCell };

export type PbDetailsStateEvent =
  PbDetailsStateLoadEvent
  | PbDetailsStateMergeOnCalculateEvent
  | PbDetailsStateMergeOnSaveEvent
  | PbDetailsStateSelectTabEvent
  | PbDetailsStateNavigateToInvalidEvent
  | PbDetailsStateSetMasterInfoEvent
  | PbDetailsStateSetDefaultsEvent
  | PbDetailsStateSetRootFieldEvent
  | PbDetailsStateUpdateHpFilterEvent
  | PbDetailsStateHousePlanAddSet
  | PbDetailsStateHousePlanUpdate
  | PbDetailsStateHousePlanRemove
  | PbDetailsStateHousePlanSelect
  | PbDetailsStateCostCenterAddEvent
  | PbDetailsStateCostCenterRemoveEvent
  | PbDetailsStateCostCenterSelectEvent
  | PbDetailsStateCostCenterLoadEvent
  | PbDetailsStateStructureSetFieldEvent
  | PbDetailsStateStructureAddEvent
  | PbDetailsStateStructureRemoveEvent
  | PbDetailsStateStructureSelectEvent
  | PbDetailsStateDetailsResetFocusedCellEvent;

export enum PbFocusedTable { ComponentList, ItemList }

export type PbDetailsSelection = {
  tab: PbDetailsTab;
  costCenterId?: ID;
  housePlan?: PbHousePlan;
  structureId?: ID;
  structureFocusedCell?: FocusedCell;
};

export enum PbDetailsTab {
  PRICE_BOOK = 'PRICE_BOOK',
  HOUSE_PLANS = 'HOUSE_PLANS',
  ITEMS_AND_COMPONENTS = 'ITEMS_AND_COMPONENTS',
  MEMO = 'MEMO',
}

export interface PbDetailsState {
  formMode: FormMode;
  priceBookId?: ID;
  sourcePriceBookId?: ID;
  memo: string;
  version?: EntityVersion;
  masterInfo: FormStateSnapshot<PbMasterInfo>;
  defaults: FormStateSnapshot<PbDefaults>;
  housePlanFilter: PbHousePlanFilter;
  dirtyHousePlans: Table<PbHousePlan>;
  costCenters: Table<PbCostCenter>;
  structures: Table<PbCcStructure>;
  isDirty: boolean;
  isRecalculationNeeded: boolean;
  selection: PbDetailsSelection;
  etag?: string;
  dirtyType?: DirtyType;
}

export const pbHousePlanTable = createTableDriver(
  getPbHousePlanId,
  createOrderIndexDriver('existing', getPbHousePlanId, whenExisting, valueComparator<PbHousePlan>(getPbHousePlanId)),
  createOrderIndexDriver('modified', getPbHousePlanId, whenModified, valueComparator<PbHousePlan>(getPbHousePlanId)));

export const ccComparator = composeComparators<PbCostCenter>(
  valueComparator('code'),
  valueComparator(getPbCostCenterId));

export const structureComparator = composeComparators<PbCcStructure>(
  newEntityComparator,
  valueComparator('code'),
  valueComparator(getPbCcStructureId));

export const pbCcTable = createTableDriver(
  getPbCostCenterId,
  createOrderIndexDriver('existing', getPbCostCenterId, whenExisting, ccComparator));

export const pbStructureTable = createTableDriver(
  getPbCcStructureId,
  createOrderIndexDriver('existing', getPbCcStructureId, whenExisting, structureComparator),
  createOrderIndexDriver('errors', getPbCcStructureId, overEvery([isEntityInvalid, whenExisting]), structureComparator),
  createGroupIndexDriver(
    'costCenter',
    getPbCcStructureId,
    whenExisting,
    (structure) => structure.costCenterId,
    structureComparator));

export const PB_DETAILS_INIT_STATE: PbDetailsState = {
  formMode: FormMode.ViewMode,
  memo: '',
  version: -1,
  masterInfo: <any>{},
  defaults: <any>{},
  housePlanFilter: { isSalesEstimate: false, isQuickQuote: false },
  dirtyHousePlans: pbHousePlanTable.create(),
  costCenters: pbCcTable.create(),
  structures: pbStructureTable.create(),
  isDirty: false,
  isRecalculationNeeded: false,
  selection: {
    tab: PbDetailsTab.PRICE_BOOK,
  },
};

export const queryIsCloning = (state: PbDetailsState) => isNil(state.priceBookId) && !isNil(state.sourcePriceBookId);
export const queryPriceBookId = (state: PbDetailsState) => state.priceBookId;
export const queryPbDefaults = (state: PbDetailsState) => state.defaults;
export const queryPbMasterInfo = (state: PbDetailsState) => state.masterInfo;
export const queryMemo = (state: PbDetailsState) => state.memo;
export const queryHousePlanFilter = (state: PbDetailsState) => state.housePlanFilter;
export const queryDirtyHousePlans = (state: PbDetailsState) => state.dirtyHousePlans;
export const queryHousePlanRequest = (state: PbDetailsState) => ({
  priceBookId: state.priceBookId,
  version: state.version,
  etag: state.etag,
  isSalesEstimate: state.housePlanFilter.isSalesEstimate,
  isQuickQuote: state.housePlanFilter.isQuickQuote,
  housePlans: queryModifiedHousePlans(state).map(pbHousePlanToRowDto),
});
export const queryHousePlan = curryRight((state: PbDetailsState, housePlanId: ID) =>
  pbHousePlanTable.byId(state.dirtyHousePlans, housePlanId));
export const queryPbSelectedHousePlan = (state: PbDetailsState) => state.selection.housePlan;
export const queryExistingHousePlans = (state: PbDetailsState) =>
  pbHousePlanTable.byOrder(state.dirtyHousePlans, 'existing');
export const queryModifiedHousePlans = (state: PbDetailsState) => {
  if (queryIsCloning(state)) {
    return pbHousePlanTable.byOrder(state.dirtyHousePlans, 'existing').map(setNewDirtyType);
  } else {
    return pbHousePlanTable.byOrder(state.dirtyHousePlans, 'modified');
  }
};

export const querySaveModel = (state: PbDetailsState) => {
  const { priceBookId, version, etag, masterInfo, defaults, memo } = state;
  const { name, releaseDate, withdrawalDate, usage } = getFormStateSnapshotValue(masterInfo);
  const defaultsValue = getFormStateSnapshotValue(defaults);

  return {
    priceBookId, version, etag,
    name, releaseDate, withdrawalDate, usage: <PriceBookUsage>usage, memo,
    defaults: defaultsValue,
    costCenters: queryModifiedCostCenters(state),
    items: queryModifiedStructures(state),
    housePlans: queryModifiedHousePlans(state),
    isPersist: false,
  };
};
export const querySelectedTab = (state: PbDetailsState) => state.selection.tab;
export const queryInvalidStructures = (state: PbDetailsState) => pbStructureTable.byOrder(state.structures, 'errors');
export const hasPbMasterInfoErrors = (masterInfo: FormStateSnapshot<PbMasterInfo>) =>
  hasFormStateError(masterInfo)
  || !!validatePbMasterInfo(getFormStateSnapshotValue(masterInfo));
export const hasPbDefaultsErrors = hasFormStateError;
export const queryHasPbStructureErrors = (state: PbDetailsState) => queryInvalidStructures(state).length > 0;
export const queryPbHasErrors = (state: PbDetailsState) =>
  hasPbMasterInfoErrors(state.masterInfo)
  || hasPbDefaultsErrors(state.defaults)
  || queryHasPbStructureErrors(state);

export const queryExistingCostCenters = (state: PbDetailsState) => pbCcTable.byOrder(state.costCenters, 'existing');
export const queryModifiedCostCenters = (state: PbDetailsState) => {
  if (queryIsCloning(state)) {
    return pbCcTable.all(state.costCenters).map(setNewDirtyType);
  } else {
    return pbCcTable.all(state.costCenters).filter(whenModified);
  }
};

export const queryCostCenter = (state: PbDetailsState, id: ID) => pbCcTable.byId(state.costCenters, id);
export const querySelectedCostCenterId = (state: PbDetailsState) => state.selection.costCenterId;

export function pbCostCenterFromFinder(found: CostCenterFinderListItem): PbCostCenter {
  return {
    costCenterId: found.id,
    code: found.code,
    name: found.name,
    isEmpty: true,
    preloaded: true,
  };
}

export const queryStructures = (state: PbDetailsState) => pbStructureTable.all(state.structures);
export const queryPbStructure = (state: PbDetailsState, structureId: ID) =>
  pbStructureTable.byId(state.structures, structureId);
export const queryCostCenterStructures = (state: PbDetailsState, costCenterId: ID) =>
  pbStructureTable.byGroup(state.structures, 'costCenter', costCenterId);
export const queryExistingStructures = (state: PbDetailsState) =>
  pbStructureTable.byOrder(state.structures, 'existing');
export const queryModifiedStructures = (state: PbDetailsState) => {
  if (queryIsCloning(state)) {
    return pbStructureTable.all(state.structures).map(setNewDirtyType);
  } else {
    return pbStructureTable.all(state.structures).filter(whenModified);
  }
};
export const queryModifiedStructuresForCostCenter = (state: PbDetailsState, costCenterId: ID) =>
  queryCostCenterStructures(state, costCenterId).filter(whenModified);

export const queryGetCostCenterRequest = (state: PbDetailsState, costCenterId: ID) => ({
  priceBookId: queryPriceBookId(state)!,
  version: state.version,
  costCenterId,
  items: queryModifiedStructuresForCostCenter(state, costCenterId),
});

export const querySelectedStructureId = (state: PbDetailsState) => state.selection.structureId;
export const queryStructureFocusedCell = (state: PbDetailsState) => state.selection.structureFocusedCell;
export const queryPbCostCenterHasItems = (state: PbDetailsState, costCenterId: ID) => {
  const cc = queryCostCenter(state, costCenterId);

  if (isNil(cc)) {
    return false;
  }

  return isCcNotPreloaded(cc) ? !cc.isEmpty : queryCostCenterStructures(state, costCenterId).length > 0;
};
